import validate from "../libs/validate-0.12.0.js";
import {validateVatNumber} from "./jsvat";
import {trans} from "../i18n/i18n";
import {trackEvent} from "../tracking/gtm";

validate.validators.presence.message = trans("validation.required");
validate.validators.length.tooShort = trans("validation.min.string", {min: "%{count}"});
validate.validators.length.tooLong = trans("validation.max.string", {max: "%{count}"});
validate.validators.url.message = trans("validation.url");
validate.validators.email.message = trans("validation.email");
validate.validators.format.message = trans("validation.regex");
validate.validators.numericality.notValid = trans("validation.numeric");
validate.validators.numericality.notInteger = trans("validation.integer");
validate.validators.numericality.notGreaterThan = trans("validation.min.numeric", {max: "%{count}"});
validate.validators.numericality.notGreaterThanOrEqualTo = trans("validation.gte.numeric", {value: "%{count}"});
validate.validators.numericality.notEqualTo = trans("validation.eq.numeric", {value: "%{count}"});
validate.validators.numericality.notLessThan = trans("validation.max.numeric", {max: "%{count}"});
validate.validators.numericality.notLessThanOrEqualTo = trans("validation.lte.numeric", {value: "%{count}"});
validate.validators.numericality.notOdd = trans("validation.odd");
validate.validators.numericality.notEven = trans("validation.even");

/**
 * @param value
 * @param options
 * @param key
 * @param attributes
 * @returns {string|undefined}
 */
validate.validators.presenceAny = function (value, options, key, attributes) {
  const otherAttrs = options.keys.map(key => attributes[key]);

  if (!validate.isEmpty(value) || otherAttrs.some(val => !validate.isEmpty(val))) {
    return;
  }

  return trans("validation.required_any");
};

/**
 * @param value
 * @param options
 * @param key
 * @param attributes
 * @returns {string|undefined}
 */
validate.validators.acceptedAny = function (value, options, key, attributes) {
  const otherAttrs = options.keys.map(key => attributes[key]);

  if (value || otherAttrs.some(val => val)) {
    return;
  }

  return trans("validation.accepted_any");
};

/**
 * @param value
 * @param options
 * @param key
 * @param attributes
 * @returns {string|undefined}
 */
validate.validators.greaterThanZeroAny = function (value, options, key, attributes) {
  const otherAttrs = options.keys.map(key => attributes[key]);

  if (value || otherAttrs.some(val => val > 0)) {
    return;
  }

  return trans("validation.gt.numeric_any", {value: 0});
};

/**
 * @param value
 * @param options
 * @returns {string|undefined}
 */
validate.validators.checked = function (value, options) {
  if (value === true || value === "1" || value === 1) {
    return;
  }

  return options.message || trans("validation.accepted");
};

/**
 *
 * @param value
 * @param options
 * @returns {*|string}
 */
validate.validators.vatIdFormat = function (value, options) {
  // Empty VAT ID isn't invalid. Its presence should be checked outside this validator (e.g. with `presence` validator)
  if (value === null) {
    return;
  }

  const countryCodePattern = /^[A-Z]{2}/;
  if (!countryCodePattern.test(value.toUpperCase())) {
    return options.message || trans("validation.vat_country_code");
  }

  if (validateVatNumber(value)) {
    return;
  }

  return options.message || trans("validation.vat_id");
};

/**
 * Validates the whole form, including any fieldsets and fields inside them.
 *
 * @param {HTMLFormElement} form
 * @param {Object} constraints
 * @param {Object} whitelist
 * @param {Object=} options
 *
 * @return {undefined|Object}
 */
export function validateForm(form, constraints, whitelist, options = {}) {
  const $form = $(form);
  const whitelistedConstraints = validate.cleanAttributes(constraints, whitelist);
  const {renderErrors = true, ...validateOptions} = options;

  try {
    let localOptions = Object.assign({fullMessages: false}, validateOptions);
  } catch (e) {
    // IE11 workaround
    var localOptions = [];
    localOptions["fullMessages"] = false;
    for (let i in validateOptions) {
      localOptions[i] = validateOptions[i];
    }
  }

  const errors = validate(form, whitelistedConstraints, localOptions);
  if (errors && renderErrors) {
    Object
        .keys(errors)
        .forEach(function (name) {
          const $el = $form.find(`[name="${name}"]`);

          try {
            rerenderValidationNew($el[0], errors[name]);
          } catch (e) {
            console.error(e);
          }
        });

    $form
        .find(".FormControlSet")
        .each((_, el) => rerenderSetValidation($(el), errors));
  }
  return errors;
}

/**
 * Validates single field (lefts other untouched whether valid or not).
 * Use on "change" or "input" events via `e.target`.
 *
 * @param {HTMLFormElement} form
 * @param {HTMLElement} el
 * @param {Object} constraints
 * @param {Object} whitelist
 * @param {Object=} options
 *
 * @return {undefined|Object}
 */
export function validateField(form, el, constraints, whitelist, options = {}) {
  const $el = $(el);

  try {
    var singleConstraints = validate.cleanAttributes(constraints, Object.assign({[el.name]: true}, whitelist));
    var localOptions = Object.assign({fullMessages: false}, options);
  } catch (e) {
    // IE11 workaround
    var singleConstraints = [];
    singleConstraints[el.name] = true;
    for (let i in whitelist) {
      singleConstraints[i] = whitelist[i];
    }

    var localOptions = [];
    localOptions["fullMessages"] = false;
    for (let i in options) {
      localOptions[i] = options[i];
    }
  }

  const errors = validate(form, singleConstraints, localOptions);

  rerenderValidationNew(el, errors && errors[el.name]);

  return errors;
}

/**
 * Turns an attributes object to whitelist object (all values = `true`).
 *
 * @param {HTMLElement} el
 * @returns {Object}
 */
export function collectWhitelist(el) {
  const values = validate.collectFormValues(el);

  return Object
    .keys(values)
    .reduce(function (acc, name) {
      acc[name] = true;

      return acc;
    }, {});
}

/**
 * Renders Bootstrap 5 validation feedback for a single input element.
 * Supports both standard and .form-floating structures.
 *
 * @param {HTMLElement} inputEl - The input element to validate.
 * @param {string[] | undefined} errors - Array of error messages, or undefined if no error.
 */
export function rerenderValidationNew(inputEl, errors) {
  if (!(inputEl instanceof HTMLElement)) return;

  // Remove previous validation classes
  inputEl.classList.remove("is-valid", "is-invalid");

  // Remove any existing feedback elements
  const isCheckbox = inputEl.type === 'checkbox';
  const parent = isCheckbox
    ? (inputEl.closest('.form-check') || inputEl.parentElement)
    : (inputEl.closest('.form-floating') || inputEl.parentElement);

  Array.from(parent.querySelectorAll('.invalid-feedback, .valid-feedback')).forEach(el => el.remove());
  // If we previously inserted feedback after the checkbox container, remove it as well
  if (isCheckbox && parent.nextElementSibling && (parent.nextElementSibling.classList.contains('invalid-feedback') || parent.nextElementSibling.classList.contains('valid-feedback'))) {
    parent.nextElementSibling.remove();
  }

  // For checkboxes, try to find the visible label to place the error under it
  let checkboxLabel = null;
  if (isCheckbox) {
    checkboxLabel = inputEl.closest('label');
    if (!checkboxLabel && inputEl.id) {
      checkboxLabel = parent.querySelector(`label[for="${inputEl.id}"]`) || document.querySelector(`label[for="${inputEl.id}"]`);
    }
  }

  // Trimmed value and length for reuse
  const value = inputEl.value.trim();
  const valueLength = value.length;

  // Only proceed if there are no prior errors
  const noPrevErrors = !errors || errors.length === 0;

  // Condition: required field is empty
  const isRequiredError = noPrevErrors && inputEl.required && value === '';

  // Condition: minlength is set, non-empty value, but too short
  const isMinLengthError =
    noPrevErrors &&
    inputEl.minLength > 0 &&
    valueLength > 0 &&
    valueLength < inputEl.minLength;

  if (isRequiredError) {
    errors = [trans('validation.required')];
  } else if (isMinLengthError) {
    const msg = trans('validation.min.string', {
      attribute: inputEl.name,
      min: inputEl.minLength
    });
    errors = [msg];
  }

  if (errors && errors.length > 0) {
    inputEl.classList.add("is-invalid");

    const feedback = document.createElement("div");
    feedback.className = "invalid-feedback";
    feedback.textContent = errors[0];

    // For checkbox inputs, show the error under the label by placing it after the whole container
    if (isCheckbox) {
      // Make sure it renders visibly even without Bootstrap's sibling selectors
      feedback.classList.add('d-block');
      parent.insertAdjacentElement("afterend", feedback);
    // If using form-floating, insert at the end of the container
    } else if (parent.classList.contains('form-floating')) {
      parent.appendChild(feedback);
    } else {
      inputEl.insertAdjacentElement("afterend", feedback);
    }

    // Send the event to GTM only if the field is not focused (i.e., when the user doesn't type anymore)
    if (document.activeElement !== inputEl) {
      trackEvent(
        "form_error",
        {
          form_error_text: errors[0],
          form_error_input: inputEl.id || inputEl.name || "Unknown field",
        }
      );
    }
  } else {
    inputEl.classList.add("is-valid");
  }
}

/**
 * @param {jQuery} $set
 * @param {undefined|Object} errors
 */
function rerenderSetValidation($set, errors) {
  if ($set.data("vd-no-validation") !== undefined) {
    return;
  }

  const myErrors = validate.cleanAttributes(errors, collectWhitelist($set));
  const hasErrors = Object.keys(myErrors).length > 0;

  $set
    .toggleClass("is-success", !hasErrors)
    .toggleClass("is-error", hasErrors)
    .find(".FormControlSet-message").remove();

  if (hasErrors) {
    $set.append(renderSetError());
  } else {
    $set.append(renderSetSuccess());
  }
}

/**
 * @param {string} msg
 *
 * @returns {string}
 */
function renderBoxError(msg) {
  return `
<span class="invalid-feedback d-block">${msg}</span>`;
}

/**
 * @returns {string}
 */
function renderSetError() {
  return `
<span class="FormControlSet-message FormControlSet-message--error">
  ${trans("status.There are errors")}
</span>`;
}

/**
 * @returns {string}
 */
function renderSetSuccess() {
  return `
<span class="FormControlSet-message FormControlSet-message--success">
  ${trans("status.All good")}
</span>`;
}
